import { Alert, Anchor, Card, Group, SimpleGrid, Stack, Text } from "@mantine/core";
import { Link } from "react-router-dom";
import { IconAlertTriangle, IconShieldCheck, IconShieldOff } from "@tabler/icons-react";
import { FieldRow } from "./DetailGrid";
import { Mono } from "./Mono";
import { StatusBadge } from "./StatusBadge";
import { Timestamp } from "./Timestamp";
import { RelatedLink } from "./RelatedLink";
import { AdminActionButton } from "./AdminActionButton";
import { EmptyState, ErrorState, LoadingState } from "./states";
import { useFirestoreDoc } from "@/firestore/hooks";
import { COLLECTIONS } from "@/firestore/collections";
import type { OwnerKeyBackupDoc, WalletDoc, WithDocId } from "@/firestore/types";
import { entityPath } from "@/routes/paths";
import { humanize } from "@/lib/format";
import { supportRecoverOwnerKeyBackup } from "@/api/actions";

/**
 * Owner-key backup status for a wallet. **Metadata only.**
 *
 * ⚠ WHAT THIS CARD CAN AND CANNOT DO — the point of the whole feature.
 *
 * A user's owner key (their BIP-39 recovery phrase, or the raw private key on pre-BIP-39 wallets)
 * is encrypted ON THEIR DEVICE under a key derived from a password they chose. The backend never
 * receives that password; it stores only ciphertext, in a non-evented Spanner side table that is
 * never projected to Firestore. So this card can answer "does a backup exist, when was it
 * written, under which algorithm suite, for which key scope" — and nothing else.
 *
 * There is NO ciphertext on this screen, NO decryption path in this console or in the backend,
 * and NO support-assisted recovery. Not for support, not for an operator, not under subpoena.
 * That is a structural property (no key exists on our side), not a policy we enforce, so it
 * cannot be undone by a config change or a role grant. The disabled "Recover for user" button is
 * a deliberate tombstone: it answers the question in the place an operator will ask it.
 *
 * This surface is read-only. Backup removal belongs to the owner or the backend account-deletion
 * lifecycle; there is no administrator backup-delete endpoint.
 *
 * Tri-state discipline: "no backup" is rendered ONLY on a loaded-and-genuinely-absent read.
 * Treating a listener error as an empty result would tell an operator a protected user is
 * unprotected (or the reverse), which is exactly the class of bug that once rendered a
 * fully-granted wallet as half-granted on mobile.
 */
export function OwnerKeyBackupCard({ wallet }: { wallet: WithDocId<WalletDoc> }) {
  // The backup is keyed on the KEY SCOPE — the root of the migration chain — not on the wallet
  // id. A version-upgrade successor inherits `rootWallet`, so it inherits the backup; only an
  // owner rotation mints a new scope, and therefore a new row. Reading by `wallet.id` instead
  // would falsely report "no backup" on every migrated wallet.
  const keyScope = wallet.rootWallet ?? wallet.id;
  const isSuccessor = keyScope !== wallet.id;

  const {
    data: backup,
    loading,
    error,
  } = useFirestoreDoc<OwnerKeyBackupDoc>(COLLECTIONS.ownerKeyBackups, keyScope);

  return (
    <Card withBorder radius="md" padding="lg">
      <Group justify="space-between" mb="xs" wrap="nowrap">
        <Text fw={600}>Owner-key backup</Text>
        {backup ? <StatusBadge state={backup.state} /> : null}
      </Group>
      <Text size="sm" c="dimmed" mb="md">
        Whether this user can recover their wallet on a new device without their written-down
        phrase. Metadata only — we hold ciphertext encrypted under a password we never receive, so
        nothing here can reveal a recovery phrase and no operator path exists to do so.
      </Text>

      {loading ? (
        <LoadingState label="Loading backup status…" />
      ) : error ? (
        // Never fall through to "no backup" on an error: an unread projection is not proof of
        // absence, and reporting it as absence is worse in both directions.
        <ErrorState error={error} />
      ) : !backup ? (
        <EmptyState
          icon={<IconShieldOff size={22} />}
          title="No backup enrolled"
          description={
            isSuccessor
              ? "No owner-key backup exists for this wallet's key scope. If the user rotated their owner key, any earlier backup covers the predecessor scope only — check the user's other wallets."
              : "This user has never enrolled an owner-key backup. If they lose their device without their written-down recovery phrase, their funds are unrecoverable — by us as well as by them."
          }
        />
      ) : (
        <Stack gap="md">
          {/*
            Free defensive cross-check: the wallet has been migrated away, yet the backup for its
            scope still claims `active`. On an owner rotation the supersede consumer should have
            flipped it. A Causa event handler that throws ACKs as 200 and drops the event, so a
            broken consumer fails silently and the row lies — this is the one failure on this
            surface that quietly costs a user their money, and the console can spot it for free.
          */}
          {wallet.migratedToWallet && backup.state === "active" ? (
            <Alert
              color="red"
              variant="light"
              icon={<IconAlertTriangle size={18} />}
              title="Backup claims active, but this wallet has been migrated"
            >
              <Stack gap={4}>
                <Text size="sm">
                  The supersede consumer may not have run. If the migration rotated the owner key,
                  this backup no longer protects the live key and the user is being told otherwise.
                  Verify against the migration before advising them.
                </Text>
                <Group gap="xs">
                  <Text size="sm">Migrated to</Text>
                  <RelatedLink entity="wallet" id={wallet.migratedToWallet} />
                </Group>
              </Stack>
            </Alert>
          ) : null}

          {backup.state === "superseded" ? (
            <Alert
              color="orange"
              variant="light"
              icon={<IconAlertTriangle size={18} />}
              title="Superseded — this backup no longer protects the live key"
            >
              <Stack gap={4}>
                <Text size="sm">
                  {backup.supersededReason === "rawKeyImported"
                    ? "The user imported a raw private key over this scope, so the backed-up phrase no longer derives the key in use."
                    : "The owner key was rotated, which mints a new key scope. The user needs to enrol a new backup for the successor wallet."}{" "}
                  This row is kept, not deleted: it is still the only recoverable copy of the
                  predecessor key, which can retain dust or an in-flight leg.
                </Text>
                {backup.supersededByWallet ? (
                  <Group gap="xs">
                    <Text size="sm">Superseded by</Text>
                    <RelatedLink entity="wallet" id={backup.supersededByWallet} />
                  </Group>
                ) : null}
              </Stack>
            </Alert>
          ) : null}

          {backup.state === "deleted" ? (
            <Alert color="gray" variant="light" icon={<IconShieldOff size={18} />} title="Removed">
              Removed from recovery on{" "}
              <Timestamp value={backup.deletedAt ?? backup.updatedAt} absolute />. This metadata row
              records the removal. Encrypted copies may remain temporarily in retained database
              history or backups.
            </Alert>
          ) : null}

          {backup.state === "active" && !wallet.migratedToWallet ? (
            <Alert color="green" variant="light" icon={<IconShieldCheck size={18} />}>
              A current backup exists for this wallet&apos;s key scope. The user can restore on a
              new device with their recovery password — we still cannot.
            </Alert>
          ) : null}

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md" verticalSpacing="md">
            <FieldRow label="Exists" value={backup.state === "deleted" ? "No (removed)" : "Yes"} />
            <FieldRow label="Enrolled" value={<Timestamp value={backup.createdAt} absolute />} />
            <FieldRow
              label="Last written"
              value={<Timestamp value={backup.backedUpAt} absolute />}
            />
            <FieldRow label="Algorithm" value={<Mono value={backup.algorithm} copy={false} />} />
            <FieldRow label="Backup version" value={backup.backupVersion ?? "—"} />
            <FieldRow
              label="Secret type"
              value={backup.secretType ? humanize(backup.secretType) : "—"}
            />
            <FieldRow
              label="Covers key scope"
              value={
                <Group gap="xs" wrap="nowrap">
                  <RelatedLink entity="wallet" id={keyScope} />
                  {isSuccessor ? (
                    <Text size="xs" c="dimmed">
                      (root of this wallet&apos;s chain)
                    </Text>
                  ) : (
                    <Text size="xs" c="dimmed">
                      (this wallet)
                    </Text>
                  )}
                </Group>
              }
            />
            <FieldRow
              label="Wallet at write time"
              value={<RelatedLink entity="wallet" id={backup.account} />}
            />
            <FieldRow label="Owner address" value={<Mono value={backup.ownerAddress} short />} />
          </SimpleGrid>

          <Group justify="space-between" wrap="nowrap">
            <Anchor
              component={Link}
              to={entityPath("ownerKeyBackup", keyScope)}
              size="sm"
              underline="hover"
            >
              Open backup record ↗
            </Anchor>
            <AdminActionButton action={supportRecoverOwnerKeyBackup} params={{ keyScope }} />
          </Group>
        </Stack>
      )}
    </Card>
  );
}
